import React, { useState } from "react";
import { Link } from "react-router-dom";
import Button from "./Button";

const ContactForm = ({ description }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    needs: "",
    agreedToPrivacy: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [submitMessage, setSubmitMessage] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    // Clear submit status when user makes changes
    if (submitStatus) {
      setSubmitStatus(null);
      setSubmitMessage("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    if (!formData.needs.trim()) {
      newErrors.needs = "Please describe your needs";
    }

    if (!formData.agreedToPrivacy) {
      newErrors.agreedToPrivacy = "You must agree to the privacy policy";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("email", formData.email.trim());
      formDataToSend.append("company", formData.company.trim());
      formDataToSend.append("message", formData.needs.trim());
      formDataToSend.append("_replyto", formData.email.trim());

      const response = await fetch("https://formspree.io/f/mnnrgbrq", {
        method: "POST",
        body: formDataToSend,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setSubmitStatus("success");
        setSubmitMessage("Thank you! Your message has been sent successfully.");
        
        // Reset form
        setFormData({
          name: "",
          email: "",
          company: "",
          needs: "",
          agreedToPrivacy: false,
        });
      } else {
        const data = await response.json();
        throw new Error(data.error || "Failed to send your message.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
      setSubmitMessage(
        error.message || "Failed to send your message. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="text-white lg:pl-8 px-4 lg:px-0">
      <div className="mb-6 sm:mb-8">
        <p className="text-primary-100 text-base sm:text-lg opacity-90">
          {description}
        </p>
      </div>

      {/* Success/Error Messages */}
          {submitStatus && (
        <div
          className={`mb-6 p-4 rounded-xl backdrop-blur-sm ${
            submitStatus === "success"
              ? "bg-green-500/20 border border-green-400/50 text-green-100 shadow-lg shadow-green-500/10"
              : "bg-red-500/20 border border-red-400/50 text-red-100 shadow-lg shadow-red-500/10"
          }`}
        >
          <p className="text-sm font-medium">{submitMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-bold mb-2 text-white tracking-wide"
          >
            Your name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 rounded-xl bg-white/10 backdrop-blur-sm border ${
              errors.name
                ? "border-red-400/60 shadow-red-500/20"
                : "border-white/20"
            } text-white placeholder-gray-300/70 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/60 focus:bg-white/15 transition-all duration-300 text-base shadow-sm hover:border-white/30`}
            placeholder=""
            disabled={isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-300">{errors.name}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-bold mb-2 text-white tracking-wide"
          >
            Work email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg bg-[#ffffff1a] border ${
              errors.email
                ? "border-red-500/50"
                : "border-[#ffffff33]"
            } text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all text-base`}
            placeholder=""
            disabled={isSubmitting}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-300">{errors.email}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="company"
            className="block text-sm font-bold mb-2 text-white tracking-wide"
          >
            Company name
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg bg-[#ffffff1a] border ${
              errors.company
                ? "border-red-500/50"
                : "border-[#ffffff33]"
            } text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all text-base`}
            placeholder=""
            disabled={isSubmitting}
          />
          {errors.company && (
            <p className="mt-1 text-sm text-red-300">{errors.company}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="needs"
            className="block text-sm font-bold mb-2 text-white tracking-wide"
          >
            Describe your needs
          </label>
          <textarea
            id="needs"
            name="needs"
            value={formData.needs}
            onChange={handleChange}
            rows="3"
            className={`w-full text-sm sm:text-base px-4 sm:px-5 py-3 sm:py-3.5 rounded-xl bg-white/10 backdrop-blur-sm border ${
              errors.needs
                ? "border-red-400/60 shadow-red-500/20"
                : "border-white/20"
            } text-white placeholder-gray-300/70 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/60 focus:bg-white/15 transition-all duration-300 resize-none shadow-sm hover:border-white/30`}
            placeholder="Tell us about your project requirements..."
            disabled={isSubmitting}
          />
          {errors.needs && (
            <p className="mt-1 text-sm text-red-300">{errors.needs}</p>
          )}
        </div>

        <div className="pt-2">
          <label className="flex items-start gap-2 cursor-pointer group">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                name="agreedToPrivacy"
                checked={formData.agreedToPrivacy}
                onChange={handleChange}
                className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-white/30 bg-white/10 backdrop-blur-sm checked:bg-white checked:border-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-white/50 focus:ring-2 focus:ring-white/40"
                disabled={isSubmitting}
              />
              <svg
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none opacity-0 peer-checked:opacity-100 text-[#1A4731] transition-opacity"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M3.5 7L5.5 9L10.5 4"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="text-sm text-gray-200 group-hover:text-white transition-colors leading-tight pt-0.5">
              I agree to the storage and processing of my personal data by
              Estabild.
            </span>
          </label>
          {errors.agreedToPrivacy && (
            <p className="mt-1 text-sm text-red-300 ml-7">
              {errors.agreedToPrivacy}
            </p>
          )}
        </div>

        <div className="text-xs text-white-400 mt-6 leading-relaxed opacity-80">
          By submitting the form you are agreeing to receive other notifications
          from Estabild. You can cancel these notifications at any time. For
          more information:{" "}
          <Link
            to="/privacy"
            className="underline hover:text-white decoration-white/50 hover:decoration-white"
          >
            Privacy Policy
          </Link>
        </div>

        <div className="pt-4 sm:pt-6 flex justify-center">
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="primary"
            className={`!font-bold !bg-white !text-[#1A4731] hover:!bg-gray-50 hover:!text-[#1A4731] px-12 sm:px-16 py-3 sm:py-4 text-base sm:text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 active:scale-100 transition-all duration-300 w-full sm:w-auto rounded-xl ${
              isSubmitting
                ? "opacity-50 cursor-not-allowed hover:!scale-100"
                : ""
            }`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
